import { Facility, LocationSearchParams } from '@gyym/sdk'
import React from 'react'

import { usePrevious } from '@/hooks/usePrevious'

import { useFacilities } from './useFacilities'

export const useMapFacilities = (
  params: Partial<LocationSearchParams>,
  shouldFetch = true,
) => {
  const { data, ...rest } = useFacilities(params, shouldFetch, {
    // Don't revalidate on focus
    revalidateOnFocus: false,
  })

  const facilities = data?.data

  const conditionKey = React.useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { latitude, longitude, radius, ...rest } = params
    return JSON.stringify(rest)
  }, [params])

  const prevConditionKey = usePrevious(conditionKey)

  type F = Facility & { lat: number; lng: number }
  const [cachedFacilities, setCachedFacilities] = React.useState<{
    [id: F['id']]: F
  }>({})

  React.useEffect(() => {
    if (!facilities) return

    if (conditionKey === prevConditionKey) {
      // if conditions are same, add facilities to cache
      setCachedFacilities((cached) =>
        facilities
          .filter((f) => f.lat && f.lng)
          .reduce((acc, f) => ({ ...acc, [f.id]: f }), cached),
      )
    } else {
      // if conditions are different, reset cache and add new data
      setCachedFacilities(
        facilities
          .filter((f) => f.lat && f.lng)
          .reduce((acc, f) => ({ ...acc, [f.id]: f }), {}),
      )
    }
  }, [facilities, conditionKey, prevConditionKey])

  return {
    data: cachedFacilities ? cachedFacilities : {},
    meta: data?.meta,
    conditionChanged: conditionKey !== prevConditionKey,
    ...rest,
  }
}
